import { useState, useEffect } from 'react';
import { db } from '../firebaseConfig';
import { collection, getDocs, query, where, orderBy, onSnapshot, doc } from 'firebase/firestore';

export const useSedes = (setError) => {
  const [sedes, setSedes] = useState([]);
  const [loadingSedes, setLoadingSedes] = useState(true);

  useEffect(() => {
    setLoadingSedes(true);
    const q = query(collection(db, 'eventSpaces'), where('type', '==', 'sede'));
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const sedesData = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setSedes(sedesData);
      setLoadingSedes(false);
    }, (error) => {
      setError("Error fetching sedes: " + error.message);
      console.error("Error fetching sedes: ", error);
      setLoadingSedes(false);
    });

    return () => unsubscribe();
  }, [setError]);

  return { sedes, loadingSedes };
};

export const useServices = (setError) => {
  const [services, setServices] = useState([]);
  const [loadingServices, setLoadingServices] = useState(true);

  useEffect(() => {
    const fetchServices = async () => {
      setLoadingServices(true);
      try {
        const querySnapshot = await getDocs(collection(db, 'services'));
        const servicesData = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setServices(servicesData);
      } catch (error) {
        setError("Error fetching services: " + error.message);
        console.error("Error fetching services: ", error);
      } finally {
        setLoadingServices(false);
      }
    };
    fetchServices();
  }, [setError]);

  return { services, loadingServices };
};

export const useSpaces = (sedeId, setError) => {
  const [spaces, setSpaces] = useState([]);
  const [loadingSpaces, setLoadingSpaces] = useState(true);

  useEffect(() => {
    if (!sedeId) {
      setSpaces([]);
      setLoadingSpaces(false);
      return;
    }

    setLoadingSpaces(true);
    const q = query(
      collection(db, 'eventSpaces'),
      where('sedeId', '==', sedeId),
      where('type', '==', 'eventSpace'), // Add back the type filter
      orderBy('createdAt', 'asc')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const fetchedSpaces = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setSpaces(fetchedSpaces);
      setLoadingSpaces(false);
    }, (err) => {
      console.error("Error fetching spaces: ", err);
      setError("Error al cargar los espacios: " + err.message);
      setLoadingSpaces(false);
    });

    return () => unsubscribe();
  }, [sedeId, setError]);

  return { spaces, loadingSpaces };
};

export const usePromotions = (setError) => {
  const [promotions, setPromotions] = useState([]);
  const [loadingPromotions, setLoadingPromotions] = useState(true);

  useEffect(() => {
    setLoadingPromotions(true);
    const q = query(collection(db, 'promotions'), orderBy('createdAt', 'desc'));
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const promotionsData = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setPromotions(promotionsData);
      setLoadingPromotions(false);
    }, (error) => {
      setError("Error fetching promotions: " + error.message);
      console.error("Error fetching promotions: ", error);
      setLoadingPromotions(false);
    });

    return () => unsubscribe();
  }, [setError]);

  return { promotions, loadingPromotions };
};

export const usePromotion = (promotionId, setError) => {
  const [promotion, setPromotion] = useState(null);
  const [loadingPromotion, setLoadingPromotion] = useState(true);

  useEffect(() => {
    if (!promotionId) {
      setPromotion(null);
      setLoadingPromotion(false);
      return;
    }

    setLoadingPromotion(true);
    const docRef = doc(db, 'promotions', promotionId);
    const unsubscribe = onSnapshot(docRef, (docSnap) => {
      if (docSnap.exists()) {
        setPromotion({ id: docSnap.id, ...docSnap.data() });
      } else {
        setError("Promotion not found.");
        setPromotion(null);
      }
      setLoadingPromotion(false);
    }, (error) => {
      setError("Error fetching promotion: " + error.message);
      console.error("Error fetching promotion: ", error);
      setLoadingPromotion(false);
    });

    return () => unsubscribe();
  }, [promotionId, setError]);

  return { promotion, loadingPromotion };
};
